// Navigation par l'ancre de l'URL (« #/carte?objet=composant:erp ») : liens partageables, bouton retour du
// navigateur, aucun serveur à configurer.
import { useEffect, useState } from 'react';

export const PAGES = [
  { id: 'tableau', libelle: 'Tableau de bord', titre: 'Tableau de bord' },
  { id: 'depot', libelle: 'Dépôt des documents', titre: 'Dépôt des documents' },
  { id: 'entretien', libelle: 'Entretien', titre: 'Entretien avec Ekera' },
  { id: 'carte', libelle: 'Carte', titre: 'La carte de votre système d’information' },
  { id: 'application', libelle: 'Du besoin à l’application', titre: 'Du besoin à l’application' },
  { id: 'constats', libelle: 'Constats et scénarios', titre: 'Constats et scénarios chiffrés' },
  { id: 'corrections', libelle: 'Corrections', titre: 'Vos corrections' },
  { id: 'decisions', libelle: 'Fiches de décision', titre: 'Fiches de décision' },
  { id: 'documents', libelle: 'Documents', titre: 'Documents de l’architecte' },
  { id: 'licences', libelle: 'Licences', titre: 'Licences inutilisées' },
  { id: 'sources', libelle: 'Sources', titre: 'Sources lues par Ekera' },
  { id: 'collecteurs', libelle: 'Collecteurs', titre: 'Collecteurs sur site' },
  { id: 'exports', libelle: 'Exports et journal', titre: 'Exports et journal des écritures' },
  { id: 'circuits', libelle: 'Circuit de validation', titre: 'Circuit de validation' },
  { id: 'cles', libelle: 'Mes clés', titre: 'Mes clés de sécurité' },
  { id: 'interroger', libelle: 'Interroger', titre: 'Interroger la carte' },
  { id: 'retours', libelle: 'Ce qui manque', titre: 'Ce qui manque' },
  { id: 'abonnement', libelle: 'Abonnement et factures', titre: 'Abonnement et factures' },
  { id: 'aide', libelle: 'Aide', titre: 'Aide' },
] as const;

export type Page = (typeof PAGES)[number]['id'];

export interface Route {
  page: Page;
  params: URLSearchParams;
}

export function lireRoute(hash: string): Route {
  const brut = hash.replace(/^#\/?/, '');
  const [chemin = '', requete = ''] = brut.split('?');
  // Ancien écran « Outils » : fusionné dans Sources (onglet « Écritures »), l'ancien lien reste valable.
  if (chemin === 'outils') return { page: 'sources', params: new URLSearchParams({ onglet: 'ecritures' }) };
  // Lien d'une alerte du circuit : « #/circuits/<identifiant de l'instance> » (seul l'identifiant ; jamais de décision)
  const instance = /^circuits\/([0-9a-f]{32})$/.exec(chemin)?.[1];
  if (instance) return { page: 'circuits', params: new URLSearchParams({ instance }) };
  const page = (PAGES.find((p) => p.id === chemin)?.id ?? 'tableau') as Page;
  return { page, params: new URLSearchParams(requete) };
}

/** Titre (h1) de l'écran : celui de la page, sauf les onglets et la fiche d'une source. */
export function titrePage(route: Route): string {
  const base = PAGES.find((p) => p.id === route.page)?.titre ?? '';
  if (route.page === 'collecteurs') return route.params.get('collecteur') ? 'Fiche d’un collecteur' : base;
  if (route.page === 'documents') return route.params.get('livrable') ? 'Un document' : base;
  if (route.page === 'circuits' && route.params.get('instance')) return 'Une instance du circuit';
  if (route.page !== 'sources') return base;
  if (route.params.get('onglet') === 'ecritures') return 'Écritures dans les outils de l’entreprise';
  if (route.params.get('onglet') === 'ajouter') return 'Ajouter une source';
  if (route.params.get('source')) return 'Fiche d’une source';
  return base;
}

export function lienVers(page: Page, params: Record<string, string | undefined> = {}): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  const s = q.toString();
  return `#/${page}${s ? `?${s}` : ''}`;
}

export function naviguer(page: Page, params: Record<string, string | undefined> = {}): void {
  window.location.hash = lienVers(page, params);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => lireRoute(window.location.hash));
  useEffect(() => {
    const f = () => setRoute(lireRoute(window.location.hash));
    window.addEventListener('hashchange', f);
    return () => window.removeEventListener('hashchange', f);
  }, []);
  return route;
}
